import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FormField } from '@/shared/components/crud/form-field'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { apiClient } from '@/shared/lib/api/api-client'
import { addAccountSchema, type AddAccountFormValues } from '../schemas/bank-account.schema'
import { bankAccountsApi } from '../api/bank-accounts-api'
import type { ConnectivityValue } from '../lib/account-connectivity'
import { WizardCompanyField, WizardCurrencyField, type CurrencyOption, type CompanyOption } from './wizard-account-shared-fields'
import { WizardInstitutionField, type InstitutionOption } from './wizard-institution-field'
import { WizardPaymentsSection } from './wizard-payments-section'
import { useSetAccountPaymentRule } from '../lib/use-payment-rules'

interface WizardCreateAccountFieldsProps {
  connectivity: ConnectivityValue
  countryCode: string
  onDone: () => void
  onBusyChange: (busy: boolean) => void
}

const FORM_ID = 'wizard-create-account-form'

/** Create-mode fields, ported from the retired add-account-dialog.tsx. The institution
 * is picked from the country-scoped searchable dropdown (`WizardInstitutionField`);
 * selecting one autofills the still-editable BIC. */
export function WizardCreateAccountFields({
  connectivity, countryCode, onDone, onBusyChange,
}: WizardCreateAccountFieldsProps) {
  const { t } = useTranslation()
  const create = bankAccountsApi.useCreate()
  const setPaymentRule = useSetAccountPaymentRule()

  /*
   * Held here and not in the form: the account does not exist yet, and the core gives the channel its
   * own route keyed by account id. So it is chosen now and saved in a second call, once the create
   * comes back with the id — one press for whoever is filling this in, two calls underneath.
   */
  const [paymentRuleId, setPaymentRuleId] = useState<number | null>(null)

  useEffect(() => {
    onBusyChange(create.isPending)
    return () => onBusyChange(false)
  }, [create.isPending, onBusyChange])

  const { data: currencies = [] } = useQuery<CurrencyOption[]>({
    queryKey: ['currencies'],
    queryFn: () => apiClient.get<CurrencyOption[]>('/api/Currencies'),
    staleTime: 10 * 60 * 1000,
  })

  const { data: companies = [] } = useQuery<CompanyOption[]>({
    queryKey: ['companies'],
    queryFn: () => apiClient.get<CompanyOption[]>('/api/companies'),
    staleTime: 10 * 60 * 1000,
  })

  const form = useForm<AddAccountFormValues>({
    resolver: zodResolver(addAccountSchema),
    defaultValues: {
      name: '', iban: '', accountNumber: '', institutionId: 0,
      description: '', currencyId: 0, bic: '',
      companyId: 0, paymentsEnabled: false, connectivity,
    },
  })

  useEffect(() => {
    // The connectivity radio lives one level up (wizard-configure-step); keep the
    // form's value in sync in case this instance persists across a selection change.
    form.setValue('connectivity', connectivity)
  }, [connectivity, form])

  function handleInstitutionSelect(institution: InstitutionOption) {
    form.setValue('bic', institution.Bic ?? '')
  }

  async function onSubmit(values: AddAccountFormValues) {
    try {
      const created = await create.mutateAsync({ ...values, connectivity })

      /*
       * The channel is saved separately and its failure is reported separately, because the account is
       * already created by then. Rolling it back would delete something the person asked for over a
       * setting they can fix in one press; telling them exactly what is missing costs them that press.
       */
      if (paymentRuleId !== null && created?.id) {
        try {
          await setPaymentRule.mutateAsync({ accountId: created.id, paymentRuleId })
        } catch {
          toast.warning(t('bankAccounts.payments.saveFailed'))
        }
      }

      toast.success('Account created')
      onDone()
    } catch {
      toast.error('Failed to create account')
    }
  }

  return (
    <form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {/* Institution drives the BIC autofill, so it goes first; the remaining fields
          mirror the edit form (wizard-edit-account-fields) order for consistency. */}
      <WizardInstitutionField control={form.control} name="institutionId" countryCode={countryCode} onSelect={handleInstitutionSelect} />

      <FormField control={form.control} name="name" label="Account Name" required>
        {(field) => <Input placeholder="Account name" {...field} />}
      </FormField>

      <FormField control={form.control} name="description" label="Description">
        {(field) => <Input {...field} />}
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField control={form.control} name="iban" label="IBAN" required>
          {(field) => <Input placeholder="e.g. GB29NWBK60161331926819" {...field} />}
        </FormField>
        <FormField control={form.control} name="accountNumber" label="Account Number" required>
          {(field) => <Input {...field} />}
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <WizardCompanyField control={form.control} name="companyId" companies={companies} />
        <WizardCurrencyField control={form.control} name="currencyId" currencies={currencies} />
      </div>

      <FormField control={form.control} name="bic" label="BIC">
        {(field) => <Input placeholder="e.g. NWBKGB2L" {...field} />}
      </FormField>

      <WizardPaymentsSection
        paymentsEnabled={form.watch('paymentsEnabled')}
        onPaymentsEnabledChange={(value) => form.setValue('paymentsEnabled', value)}
        paymentRuleId={paymentRuleId}
        onPaymentRuleChange={setPaymentRuleId}
      />

      <div className="flex justify-end pt-2">
        <Button type="submit" form={FORM_ID} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create Account'}
        </Button>
      </div>
    </form>
  )
}
